// src/poker/table/SeatCompactPanel.js
import React from "react";
import Card from "./Card";
import { ORANGE, UI } from "./labels";
import { formatStackBB } from "./helpers";

function fmtSignedAmount(value, stackViewMode, bbAmount) {
  const x = Number(value || 0);
  if (!x) return null;
  const sign = x > 0 ? "+" : "−";
  const abs = Math.abs(x);

  if (stackViewMode === "bb" && Number(bbAmount) > 0) {
    return `${sign}${formatStackBB(abs, bbAmount)} BB`;
  }
  return `${sign}${abs}`;
}

/**
 * Kompaktowy panel seatu (do raila / małych kart).
 */
export default function SeatCompactPanel({
  title,
  badges = [],
  stack,
  committedStreet,
  lastActionText,
  extraRightText,

  // ✅ NEW: result (end of hand)
  resultText, // np. "Dwie pary" / "Para"
  resultDelta, // number (może być ujemne)

  // cards:
  cards = [],
  showCards = true,

  // show cards UX:
  canRevealPhase,
  hasPublicCards,
  onShowCards,
  showShowCardsButton,

  dense = true,

  // stack display
  stackViewMode = "chips",
  bbAmount = 0,
  onToggleStackView,

  hideStackLabel = false,
  inlineCommitted = false,
}) {
  const safeStack = stack ?? null;
  const safeCommitted = committedStreet ?? null;

  const showTop = !!title || (badges && badges.length > 0);

  const showCardsRow = showCards;
  const hasCards = Array.isArray(cards) && cards.filter(Boolean).length === 2;

  const showShowCardsUI = !!canRevealPhase && !!showShowCardsButton;
  const canClickShow = showShowCardsUI && !hasPublicCards && typeof onShowCards === "function";

  const padY = dense ? 8 : 10;
  const gap = dense ? 8 : 10;

  const dealMs = 1100;
  const dealGapMs = 160;

  const formatActionText = (txt) => {
    if (typeof txt !== "string" || !txt.trim()) return "";
    if (stackViewMode !== "bb" || !bbAmount || bbAmount <= 0) return txt;

    const m = txt.trim().match(/^(.+?)\s+(\d+(?:[.,]\d+)?)$/);
    if (!m) return txt;

    const prefix = m[1];
    const raw = m[2].replace(",", ".");
    const amountChips = Number(raw);
    if (!Number.isFinite(amountChips)) return txt;

    return `${prefix} ${formatStackBB(amountChips, bbAmount)} BB`;
  };

  const renderCommitted = () => {
    if (safeCommitted == null) return UI.NO_VALUE;
    return stackViewMode === "bb" ? `${formatStackBB(safeCommitted, bbAmount)} BB` : safeCommitted;
  };

  const resultDeltaTxt = fmtSignedAmount(resultDelta, stackViewMode, bbAmount);
  const showResultLine = !!resultText || !!resultDeltaTxt;

  return (
    <div style={{ display: "grid", gap }}>
      {showTop ? (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 900,
                lineHeight: 1.15,
                fontSize: dense ? 14 : 15,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={title}
            >
              {title}
            </div>

            {lastActionText ? (
              <div style={{ marginTop: 3, fontSize: 11, opacity: lastActionText === "—" ? 0.35 : 0.85 }}>
                <b style={{ color: ORANGE }}>{formatActionText(lastActionText)}</b>
              </div>
            ) : null}
          </div>

          {badges?.length ? (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
              {badges.map((b, idx) => (
                <Badge key={idx} text={b.text} strong={!!b.strong} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 10,
          alignItems: "baseline",
          padding: `${padY}px 0 0`,
          borderTop: "1px solid rgba(255,165,0,0.18)",
        }}
      >
        <div style={{ minWidth: 0 }}>
          {!hideStackLabel ? <div style={{ fontSize: 12, opacity: 0.75, lineHeight: 1 }}>{UI.STACK}</div> : null}
          <div style={{ fontSize: dense ? 18 : 19, fontWeight: 900, lineHeight: 1.05 }}>
            {onToggleStackView ? (
              <span
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={onToggleStackView}
                title={stackViewMode === "bb" ? "Kliknij: pokaż w żetonach" : "Kliknij: pokaż w BB"}
              >
                {safeStack != null
                  ? stackViewMode === "bb"
                    ? `${formatStackBB(safeStack, bbAmount)} BB`
                    : safeStack
                  : UI.NO_VALUE}
              </span>
            ) : (
              <>
                {safeStack != null
                  ? stackViewMode === "bb"
                    ? `${formatStackBB(safeStack, bbAmount)} BB`
                    : safeStack
                  : UI.NO_VALUE}
              </>
            )}
          </div>
        </div>

        <div style={{ textAlign: "right" }}>
          {inlineCommitted ? (
            <div style={{ fontSize: 12, lineHeight: 1.1, fontWeight: 900 }}>
              <span style={{ opacity: 0.75 }}>{UI.COMMITTED}:</span>{" "}
              <span style={{ color: ORANGE }}>{renderCommitted()}</span>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, lineHeight: 1, fontWeight: 900, color: ORANGE }}>{UI.COMMITTED}</div>
              <div style={{ fontSize: dense ? 14 : 15, fontWeight: 900, lineHeight: 1.05, color: ORANGE }}>
                {renderCommitted()}
              </div>
            </>
          )}

          {extraRightText ? (
            <div style={{ marginTop: 4, fontSize: 11, opacity: 0.7, whiteSpace: "nowrap" }}>{extraRightText}</div>
          ) : null}
        </div>
      </div>

      {/* ✅ wynik rozdania */}
      {showResultLine ? (
        <div style={{ marginTop: 2, fontSize: 12, opacity: 0.9, display: "flex", gap: 8, flexWrap: "wrap" }}>
          {resultText ? (
            <span>
              Układ: <b>{resultText}</b>
            </span>
          ) : null}
          {resultDeltaTxt ? <b style={{ color: ORANGE }}>{resultDeltaTxt}</b> : null}
        </div>
      ) : null}

      {showCardsRow ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {hasCards ? (
            <>
              <Card
                key={`deal-${cards[0]?.r || "X"}${cards[0]?.s || "X"}`}
                c={cards[0]}
                size="md"
                revealMode="wipeUp"
                revealMs={dealMs}
                revealDelayMs={0}
              />
              <Card
                key={`deal-${cards[1]?.r || "X"}${cards[1]?.s || "X"}`}
                c={cards[1]}
                size="md"
                revealMode="wipeUp"
                revealMs={dealMs}
                revealDelayMs={dealGapMs}
              />
            </>
          ) : (
            <span style={{ opacity: 0.65, fontSize: 12 }}>Brak kart</span>
          )}
        </div>
      ) : null}

      {showShowCardsUI ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {hasPublicCards ? (
            <span style={{ fontSize: 12, fontWeight: 900, color: "rgba(255,165,0,0.95)" }}>✅ Pokazałeś</span>
          ) : null}

          {canClickShow ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => onShowCards?.()}
              title="Pokaż swoje karty wszystkim (po zakończeniu rozdania)."
              style={{ padding: "6px 10px", fontSize: 12 }}
            >
              Pokaż karty
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Badge({ text, strong }) {
  return (
    <span className={`seat-badge ${strong ? "seat-badge-strong" : ""}`} style={{ whiteSpace: "nowrap" }}>
      {text}
    </span>
  );
}